import {Breadcrumb, Button, Empty, Input, Pagination, Table} from 'antd';
import { DownloadOutlined, HomeOutlined } from '@ant-design/icons';

import { Layout } from '../../components/layout';
import { RawRegulationsDrawer } from './components/raw-regulation-drawer';
import type { RawRegulationsProps, RawRegulationsType } from './RawRegulations.types.ts';

import { RAW_REGULATIONS_COLUMNS } from './RawRegulations.constants.tsx';
import { useTranslation } from 'react-i18next';

import './RawRegulations.styles.css';
import { useEffect } from 'react';

export default function RawRegulations({
  rawRegulations,
  openRawRegulationDrawer,
  onOpenRawRegulationDrawer,
  onCloseRawRegulationDrawer,
  onSubmitRawRegulation,
  regulationId,
  pageNumber,
  pageSize,
  setPageNumber,
  setPageSize,
  searchInput,
  onChangeSearchInput,
  onSearchRawRegulations,
  onExportRawRegulations,
  isRegulationsFetching,
}: RawRegulationsProps) {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = `Administration ${t('pages.admin.module.raw-regulations')}`;
  }, [t]);

  return (
    <Layout>
      <>
        <Breadcrumb
          className="breadcrumb"
          items={[
            {
              href: '/',
              title: <HomeOutlined />,
            },
            {
              href: '/',
              title: t('common.portal'),
            },
            {
              href: '/admin',
              title: t('pages.admin.title'),
            },
            {
              title: t('pages.admin.module.raw-regulations'),
            },
          ]}
        />

        <div className="topics-container">
          <div className="topics-bar">
            <h1 className="title">{t('pages.admin.module.raw-regulations')}</h1>
            <div className="topics-actions">
              <Button
                className="topics-export-button"
                shape="round"
                icon={<DownloadOutlined />}
                size="large"
                onClick={onExportRawRegulations}
              >
                {t('common.export')}
              </Button>
              <Input.Search
                placeholder={t('pages.raw-regulations.search-placeholder')}
                size="large"
                value={searchInput}
                onChange={(e) => onChangeSearchInput(e.target.value)}
                onSearch={onSearchRawRegulations}
                className="topics-search"
              />
            </div>
          </div>
        </div>
        <div className="admin-page-content">
          <Table<RawRegulationsType>
            rowKey="id"
            style={{ width: '100%' }}
            loading={isRegulationsFetching}
            columns={RAW_REGULATIONS_COLUMNS}
            locale={{
              emptyText:
                  !isRegulationsFetching
                      ? <Empty description={t('pages.raw-regulations.search-no-results')}/>
                      : undefined
            }}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            dataSource={rawRegulations?.items || []}
            pagination={false}
            onRow={(record) => {
              return {
                onClick: (event: React.MouseEvent<HTMLButtonElement>) =>
                  onOpenRawRegulationDrawer(event, record?.id),
              };
            }}
            scroll={{ x: 'max-content' }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <Pagination
              current={pageNumber}
              pageSize={pageSize}
              onShowSizeChange={(_, _pageSize) => setPageSize(_pageSize)}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              total={rawRegulations?.totalCount}
              onChange={(page) => setPageNumber(page)}
              style={{ marginTop: 16, textAlign: 'center' }}
            />
          </div>
        </div>
        {openRawRegulationDrawer && (
          <RawRegulationsDrawer
            open={openRawRegulationDrawer}
            onClose={onCloseRawRegulationDrawer}
            onSubmit={onSubmitRawRegulation}
            regulationId={regulationId}
          />
        )}
      </>
    </Layout>
  );
}
